import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronUp, FileText, Loader2, Download, ExternalLink,
  Search, ChevronLeft, ChevronRight, Filter, X, Columns3, ArrowLeft
} from 'lucide-react';
import apiService from '../config';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useLanguage } from '../StoreContext/LanguageContext';

import {
  FaFilter, FaSort, FaSortUp, FaSortDown,
  FaTimes, FaSortAlphaDown, FaSortAlphaUp
} from 'react-icons/fa';

const COLUMNS_CONFIG = {
  sr_no: { label: "SR No.", default: true, center: true },
  dist_name: { label: "District", default: true },
  taluka_name: { label: "Taluka", default: true },
  village_name: { label: "Village", default: true },
  project_name: { label: "Project Name", default: true },
  acquisition_proposal_no: { label: "Proposal No", default: true },
  documents: { label: "Documents", default: true },
};

// Main Component
export default function PublicationSection() {
  const [openSection, setOpenSection] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProposal, setSelectedProposal] = useState('');
  const [projects, setProjects] = useState([]);
  // ✅ REMOVED: proposals, loadingProposals states (fetchProposals removed)
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState(null);
  const [masterProjects, setMasterProjects] = useState([]);
  const [selectedMasterProject, setSelectedMasterProject] = useState('');
  const [loadingMasterProjects, setLoadingMasterProjects] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const [filterLegacy, setFilterLegacy] = useState('');
  const [selectedTalukaVillage, setSelectedTalukaVillage] = useState('');
  const [talukaVillages, setTalukaVillages] = useState([]);
  const [loadingTalukaVill, setLoadingTalukaVill] = useState(false);
  const [proposalList, setProposalList] = useState([]);
  const [loadingProposalList, setLoadingProposalList] = useState(false);
  const [proposalPage, setProposalPage] = useState(1);
  const PROPOSALS_PER_PAGE = 10;
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataSource, setDataSource] = useState([]);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState("all");
  const [excelFilters, setExcelFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'ID', direction: 'DESC' });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [activeFilterDropdown, setActiveFilterDropdown] = useState(null);
  const [checkListSearch, setCheckListSearch] = useState("");
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [projectStatus, setProjectStatus] = useState("ALL");
  const [viewMode, setViewMode] = useState('summary');
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [categoryLabel, setCategoryLabel] = useState('');
  const [projectTotal, setProjectTotal] = useState(null);
  const [projectLabel, setProjectLabel] = useState('');
  const [villageTotal, setVillageTotal] = useState(null);
  const [villageLabel, setVillageLabel] = useState('');
  const [columns, setColumns] = useState(
    Object.entries(COLUMNS_CONFIG).map(([key, config]) => ({
      key,
      label: config.label,
      width: key === 'sr_no' ? 80 : 180,
      visible: config.default ?? true,
      numeric: config.numeric ?? false,
    }))
  );
  const [convertedSearchText, setConvertedSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [dashboardData, setDashboardData] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [searchText, setSearchText] = useState("");
  const [docModal, setDocModal] = useState(null);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState("");
  const [isLegacyAll, setIsLegacyAll] = useState(true);
  const allMasterData = useRef([]);
  const colDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const tableRef = useRef(null);
  const draggingItem = useRef(null);
  const draggingOverItem = useRef(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedVillageId, setSelectedVillageId] = useState("");
  const { language, translations } = useLanguage();
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const [pSearch, setPSearch] = React.useState('');
  const [pSearchCol, setPSearchCol] = React.useState('all');
  const [pSort, setPSort] = React.useState({ key: '', direction: 'ASC' });
  const [pFilters, setPFilters] = useState({});
  const [pActiveFilter, setPActiveFilter] = React.useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const fetchProposalAbortRef = useRef(null);
  const proposalFetchIdRef = useRef(0);
  // Add this state
  const [filterCategoryEngName, setFilterCategoryEngName] = useState('');

  // CLICK OUTSIDE TO CLOSE
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colDropdownRef.current && !colDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setActiveFilterDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!filterCategory) {
      setMasterProjects([]);
      setSelectedMasterProject('');
      setProjects([]);
      setSelectedProject('');
      setSelectedProposal('');
      setDashboardData([]);
      setTotalRecords(0);
      setProposalList([]);
      return;
    }

    fetchMasterProjects(filterCategory); // pass category_id
    fetchProposalList({ category: filterCategory });
  }, [filterCategory]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchTalukaVillages(selectedProjectId);
    } else {
      setTalukaVillages([]);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProposal) {
      fetchDashboardData(selectedProposal);
    }
  }, [currentPage, recordsPerPage, selectedProposal, projectStatus]);

  useEffect(() => {
    if (!filterCategory) return;

    fetchMasterProjects(filterCategory);

    fetchProposalList({
      category: filterCategory
    });

  }, [filterCategory]);

  useEffect(() => {
    setProposalPage(1);
  }, [proposalList]);

  const fetchMasterProjects = async (category) => {
    setLoadingMasterProjects(true);
    setError(null);
    try {
      const response = await apiService.getMasterAllProject(category);
      console.log('Master Projects Response:', response.data);
      if (response.status === 204 || !response.data || response.data.length === 0) {
        setMasterProjects([]);
      } else {
        if (response.data && response.data.length > 0) {
          console.log('First project fields:', Object.keys(response.data[0]));
          console.log('First project data:', response.data[0]);
        }
        setMasterProjects(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching master projects:', err);
      setError('Failed to load master projects.');
      setMasterProjects([]);
    } finally {
      setLoadingMasterProjects(false);
    }
  };

  const fetchProjects = async (masterProjectId, division = '', legacy = '') => {
    setLoadingProjects(true);
    setError(null);
    console.log('Fetching projects with params:', { masterProjectId, division, legacy });
    try {
      const response = await apiService.getAllProjectsBeforeLogin(masterProjectId, division, legacy);
      setProjects(response.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects.');
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // ✅ REMOVED: fetchProposals function entirely

  const fetchTalukaVillages = async (projectId) => {
    if (!projectId) {
      setTalukaVillages([]);
      return;
    }

    setLoadingTalukaVill(true);

    try {
      const response = await apiService.getTalukaVill(projectId);

      console.log("TalukaVill API Response:", response.data);

      const list = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];

      setTalukaVillages(list);

    } catch (err) {
      console.error("Error fetching taluka-village list:", err);
      setTalukaVillages([]);
    } finally {
      setLoadingTalukaVill(false);
    }
  };

  const fetchProposalList = async (params = {}) => {
    // ✅ Increment fetch ID to invalidate any in-flight previous calls
    const fetchId = ++proposalFetchIdRef.current;

    console.log("FetchProposalList Params:", params);
    try {
      setLoadingProposalList(true);
      setProposalList([]); // ✅ Clear immediately so stale data doesn't show

      let response;
      if (params.category && params.projectId && params.taluka && params.village) {
        response = await apiService.getProposalForPublicDashboard(params.projectId, params.taluka, params.village, params.category);
      } else if (params.category && params.projectId) {
        response = await apiService.getProposalForPublicDashboard(params.projectId, '', '', params.category);
      } else if (params.category) {
        response = await apiService.getProposalForPublicDashboard('', '', '', params.category);
      } else {
        response = await apiService.getProposalForPublicDashboard();
      }

      // ✅ If a newer call was made, discard this result
      if (fetchId !== proposalFetchIdRef.current) return;

      const list = response?.data?.data || response?.data || [];
      setProposalList(list);
      setTotalRecords(list.length);

      if (!params.projectId && params.category) {
        setCategoryTotal(list.length);
        setProjectTotal(null);
        setProjectLabel('');
        setVillageTotal(null);
        setVillageLabel('');
      } else if (params.projectId && !params.taluka) {
        setProjectTotal(list.length);
        setVillageTotal(null);
        setVillageLabel('');
      } else if (params.taluka && params.village) {
        setVillageTotal(list.length);
      }

    } catch (error) {
      if (fetchId !== proposalFetchIdRef.current) return; // ✅ Ignore stale errors too
      console.error("Error fetching proposals:", error);
      setProposalList([]);
      setTotalRecords(0);
    } finally {
      if (fetchId === proposalFetchIdRef.current) {
        setLoadingProposalList(false);
      }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await apiService.getAllCategory();
      const list = Array.isArray(response?.data) ? response.data : [];
      setCategories(list);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleVillageChange = (e) => {
    const value = e.target.value;
    setSelectedVillageId(value);

    if (!value || !selectedProjectId) {
      setVillageLabel('');
      setVillageTotal(null);
      return;
    }

    const [talukaName, villageName] = value.split("__");
    setVillageLabel(`${talukaName} - ${villageName}`);

    fetchProposalList({
      category: filterCategory,
      projectId: selectedProjectId,
      taluka: talukaName,
      village: villageName
    });
  };

  const convertToMarathi = async (text) => {
    try {
      if (!text) return text;
      const response = await fetch(
        "https://inputtools.google.com/request?itc=mr-t-i0-und&num=5",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: `text=${encodeURIComponent(text)}`,
        }
      );
      const data = await response.json();
      if (data[0] === "SUCCESS") {
        return data[1][0][1][0];
      }
      return text;
    } catch (error) {
      console.error("Marathi conversion error:", error);
      return text;
    }
  };

  const filteredData = dashboardData.filter((row) => {
    if (selectedTalukaVillage) {
      const [t, v] = selectedTalukaVillage.split('__');
      if (row.taluka_name !== t || row.village_name !== v) return false;
    }
    if (!searchText) return true;

    const englishSearch = searchText.toLowerCase();
    const marathiSearch = convertedSearchText.toLowerCase();

    const matchValue = (value) => {
      const original = String(value || "").toLowerCase();
      return original.includes(englishSearch) || original.includes(marathiSearch);
    };

    if (searchColumn === "all") {
      return Object.values(row).some(val => matchValue(val));
    }
    return matchValue(row[searchColumn]);
  });

  const fetchDashboardData = async (proposalIdOverride) => {
    const resolvedProposalId = proposalIdOverride ?? selectedProposal;
    if (!filterCategory || !resolvedProposalId) return;
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const apiParams = {
        page: currentPage,
        limit: recordsPerPage,
        category: filterCategory,
        proposalId: resolvedProposalId,
      };
      if (projectStatus === "COMPLETED") apiParams.legacy = 1;
      if (projectStatus === "INCOMPLETE") apiParams.legacy = 0;
      if (selectedMasterProject) apiParams.projectId = selectedMasterProject;
      if (selectedProject) apiParams.projectId = selectedProject;
      if (selectedTalukaVillage) {
        const [talukaName, villageName] = selectedTalukaVillage.split('__');
        apiParams.taluka = talukaName;
        apiParams.village = villageName;
      }
      const response = await apiService.getPublicDashboard(apiParams);

      let records = [];
      let pag = {};

      if (response && typeof response === 'object') {
        if (Array.isArray(response.data)) {
          records = response.data;
        }
        pag = response.pagination || {};
      }

      setDashboardData(records);
      setPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalRecords: pag.totalRecords || records.length,
        limit: pag.limit || recordsPerPage,
        hasNextPage: pag.hasNextPage || false,
        hasPrevPage: pag.hasPrevPage || false,
      });
      setTotalRecords(pag.totalRecords || records.length);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setDashboardError("Failed to load publication data");
      setDashboardData([]);
      setTotalRecords(0);
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const selectedEngName = e.target.value;

    const selectedCat = categories.find(
      c => c.eng_category_name === selectedEngName
    );

    const catName = selectedCat?.category_name || '';

    // Clear all dependent state
    setProposalList([]);
    setDashboardData([]);
    setTotalRecords(0);
    setMasterProjects([]);
    setSelectedMasterProject('');
    setSelectedProjectId('');
    setSelectedProject('');
    setSelectedProposal('');
    setSelectedVillageId('');
    setTalukaVillages([]);
    setViewMode('summary');
    setSearchText('');
    setConvertedSearchText('');

    setCategoryLabel(catName);
    setCategoryTotal(0);
    setProjectLabel('');
    setProjectTotal(null);
    setVillageLabel('');
    setVillageTotal(null);

    setCategoryLabel(catName);
    setFilterCategory(selectedEngName);
  };

  const handleMasterProjectChange = (e) => {
    const selectedValue = e.target.value;
    const projName = masterProjects.find(p => String(p.project_id) === String(selectedValue))?.project_name || '';

    setSelectedMasterProject(selectedValue);
    setSelectedProjectId(selectedValue);

    // ✅ Clear dependent state
    setSelectedVillageId('');
    setTalukaVillages([]);
    setSelectedProposal('');
    setProposalList([]);
    setDashboardData([]);
    setTotalRecords(0);
    setViewMode('summary');
    setVillageTotal(null);
    setVillageLabel('');

    if (selectedValue) {
      setProjectLabel(projName);
      fetchTalukaVillages(selectedValue);
      fetchProposalList({ category: filterCategory, projectId: selectedValue });
    } else {
      setProjectLabel('');
      setProjectTotal(null);
      fetchProposalList({ category: filterCategory });
    }
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;

    setSelectedProjectId(projectId);
    setSelectedVillageId('');

    if (projectId) {
      fetchTalukaVillages(projectId);

      fetchProposalList({
        category: filterCategory,
        projectId: projectId
      });
    } else {
      setProposalList([]);
    }
  };

  const handleProposalChange = (e) => {
    const value = e.target.value;
    setSelectedProposal(value);
    setCurrentPage(1);
    if (value && selectedProject) {
      setOpenSection(1);
    } else {
      setDashboardData([]);
      setTotalRecords(0);
    }
  };

  const toggleSection = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  if (error && !filterCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yellow-50/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-xl shadow-lg border border-red-200"
        >
          <p className="text-red-600 text-lg font-medium">{error}</p>
        </motion.div>
      </div>
    );
  }

  // --- EXPORT FUNCTIONS ---
  const exportToExcel = () => {
    const visibleCols = columns.filter(c => c.visible);
    const exportData = data.map(row => {
      const newRow = {};
      visibleCols.forEach(col => {
        newRow[col.label] = row[col.key] || '-';
      });
      return newRow;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "Quality_Checking_Export.xlsx");
  };

  const handleDragStart = (e, index) => {
    draggingItem.current = index;
    setDraggingIdx(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, index) => {
    draggingOverItem.current = index;
  };

  const handleDragEnd = () => {
    const from = draggingItem.current;
    const to = draggingOverItem.current;
    if (from !== null && to !== null && from !== to) {
      setColumns(prev => {
        const copyListItems = [...prev];
        const dragItemContent = copyListItems[from];
        if (!dragItemContent) return prev;
        copyListItems.splice(from, 1);
        copyListItems.splice(to, 0, dragItemContent);
        return copyListItems.filter(Boolean);
      });
    }
    draggingItem.current = null;
    draggingOverItem.current = null;
    setDraggingIdx(null);
  };

  const exportToPDF = async () => {
    if (!tableRef.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(tableRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save("Quality_Checking_Export.pdf");
    } catch (error) {
      console.error("PDF Export failed", error);
    }
    setLoading(false);
  };

  // --- HANDLERS ---
  const handleSort = (key, dir = null) => {
    setSortConfig(prev => ({ key, direction: dir || (prev.key === key && prev.direction === 'ASC' ? 'DESC' : 'ASC') }));
  };

  const handleReset = () => {
    setGlobalSearch(""); setExcelFilters({}); setCurrentPage(1);
  };

  const getUniqueValues = (key) => {
    const s = new Set();
    allMasterData.current.forEach(r => s.add(String(r[key] || '').trim() || '__BLANK__'));
    return Array.from(s).sort();
  };

  const handleExcelCheckChange = (key, val) => {
    setExcelFilters(prev => {
      const current = prev[key] || getUniqueValues(key);
      const next = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
      if (next.length === getUniqueValues(key).length) {
        const copy = { ...prev }; delete copy[key]; return copy;
      }
      return { ...prev, [key]: next };
    });
  };

  // const toggleExcelFilter = (key) => {
  //   setActiveFilterDropdown(activeFilterDropdown === key ? null : key);
  //   setCheckListSearch("");
  // };

  const toggleExcelFilter = (key, event) => {
    if (activeFilterDropdown === key) {
      setActiveFilterDropdown(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    setDropdownPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX,
    });

    setActiveFilterDropdown(key);
    setCheckListSearch("");
  };

  // --- UI HELPERS ---
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-yellow-200 ml-1" size={10} />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="text-white ml-1" /> : <FaSortDown className="text-white ml-1" />;
  };

  const handleResizerMouseDown = (e, i) => {
    e.preventDefault();
    if (!columns[i]) return;
    const startX = e.clientX, startW = columns[i].width || 140;
    const onMove = (me) => setColumns(prev => {
      const c = [...prev];
      if (c[i]) c[i] = { ...c[i], width: Math.max(50, startW + (me.clientX - startX)) };
      return c;
    });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  };

  const totalPages = pagination.totalPages || Math.ceil(totalRecords / recordsPerPage) || 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-yellow-50/30 py-8 px-4 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* ─── Header ─── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 px-6 py-5"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-yellow-500 rounded-lg shadow-md shadow-yellow-500/20">
                <FileText className="text-white w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight flex flex-wrap items-center gap-1.5">
                  <span>{translations[language]?.completedProposals}</span>
                  {categoryLabel && (
                    <>
                      <span className="text-yellow-400 mx-1">|</span>
                      <span className="text-yellow-600">"{categoryLabel}"</span>
                      <span className="text-xs font-bold text-white bg-yellow-500 px-2 py-0.5 rounded-full">
                        {categoryTotal}
                      </span>
                    </>
                  )}
                  {projectLabel && projectTotal !== null && (
                    <>
                      <span className="text-gray-400 mx-1">›</span>
                      <span className="text-blue-600 text-base">{projectLabel}</span>
                      <span className="text-xs font-bold text-white bg-blue-500 px-2 py-0.5 rounded-full">
                        {projectTotal}
                      </span>
                    </>
                  )}
                  {villageLabel && villageTotal !== null && (
                    <>
                      <span className="text-gray-400 mx-1">›</span>
                      <span className="text-green-600 text-base">{villageLabel}</span>
                      <span className="text-xs font-bold text-white bg-green-500 px-2 py-0.5 rounded-full">
                        {villageTotal}
                      </span>
                    </>
                  )}
                </h1>
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-yellow-50 hover:border-yellow-300 hover:text-yellow-700 transition-all text-sm font-medium shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>
        </motion.div>

        {/* ─── Unified Card: Filters + Search + Table ─── */}
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        >
          {/* ─── Filter Dropdowns ─── */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-4 text-yellow-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Filters</span>
              </div>

              <div className="flex flex-row gap-1">
                <button
                  onClick={() => {
                    setFilterCategory('');
                    setFilterCategoryEngName('');
                    setSelectedMasterProject('');
                    setSelectedProjectId('');
                    setSelectedProject('');
                    setSelectedProposal('');
                    setSelectedTalukaVillage('');
                    setSelectedVillageId('');
                    setTalukaVillages([]);
                    setMasterProjects([]);
                    setProjectStatus('ALL');
                    setViewMode('summary');
                    setDashboardData([]);
                    setCurrentPage(1);
                    setProposalPage(1);
                    setSearchText('');
                    setConvertedSearchText('');
                    setProposalList([]);
                    setCategoryLabel('');
                    setCategoryTotal(0);
                    setProjectLabel('');
                    setProjectTotal(null);
                    setVillageLabel('');
                    setVillageTotal(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-yellow-400 bg-yellow-50 text-yellow-700 hover:bg-yellow-500 hover:text-white hover:border-yellow-500 hover:shadow-md hover:shadow-yellow-500/20 transition-all text-sm font-semibold"
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
                {/* <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5 border border-gray-200 shadow-inner">
                  <button
                    onClick={() => setProjectStatus("ALL")}
                    className={`relative px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${projectStatus === "ALL"
                      ? "bg-blue-700 text-white shadow-md ring-2 ring-gray-400/40 scale-[1.03]"
                      : "text-gray-500 hover:bg-white/60 hover:text-blue-700"
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setProjectStatus("INCOMPLETE")}
                    className={`relative px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${projectStatus === "INCOMPLETE"
                      ? "bg-yellow-500 text-white shadow-md ring-2 ring-yellow-400/50 scale-[1.03]"
                      : "text-gray-500 hover:bg-white/60 hover:text-yellow-600"
                      }`}
                  >
                    {projectStatus === "INCOMPLETE" && <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />}
                    On Going
                  </button>
                  <button
                    onClick={() => setProjectStatus("COMPLETED")}
                    className={`relative px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${projectStatus === "COMPLETED"
                      ? "bg-green-500 text-white shadow-md ring-2 ring-green-400/50 scale-[1.03]"
                      : "text-gray-500 hover:bg-white/60 hover:text-green-600"
                      }`}
                  >
                    {projectStatus === "COMPLETED" && <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
                    Completed
                  </button>
                </div> */}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* Category */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1.5">
                  Select Category <span className="text-red-500">*</span>
                </label>

                <select
                  value={filterCategory}
                  onChange={handleCategoryChange}
                  disabled={loadingCategories}
                  className="w-full h-11 px-3 pr-8 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none truncate"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.eng_category_name}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Master Project */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1.5">
                  Select Project
                </label>

                <select
                  value={selectedProjectId}
                  onChange={handleMasterProjectChange}
                  className="w-full h-11 px-3 pr-8 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none truncate"
                >
                  <option value="">Select Project</option>
                  {masterProjects.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Taluka - Village */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1.5">
                  Select Taluka - Village
                </label>

                <select
                  value={selectedVillageId}
                  onChange={handleVillageChange}
                  disabled={!selectedProjectId}
                  className="w-full h-11 px-3 pr-8 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none truncate"
                >
                  <option value="">Select Taluka - Village</option>
                  {talukaVillages.map((item, index) => (
                    <option key={index} value={`${item.taluka_name}__${item.village_name}`}>
                      {item.taluka_name} - {item.village_name}
                    </option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          {/* ─── Summary Table (Proposal List) ─── */}
          {viewMode === 'summary' && (() => {
            const PROPOSAL_COLS = [
              { key: 'acquisition_proposal_no', label: 'Proposal No' },
              { key: 'section_11_bhusampadan_no', label: 'Bhusampadan No' },
              { key: 'dist_name', label: 'District' },
              { key: 'taluka_name', label: 'Taluka' },
              { key: 'village_name', label: 'Village' },
            ];


            const getPUniqueValues = (key) => {
              const s = new Set();
              proposalList.forEach(r => s.add(String(r[key] || '').trim() || '__BLANK__'));
              return Array.from(s).sort();
            };

            let processedList = [...proposalList];

            // Search filter
            if (pSearch) {
              const q = pSearch.toLowerCase();
              processedList = processedList.filter(p =>
                pSearchCol === 'all'
                  ? PROPOSAL_COLS.some(c => String(p[c.key] || '').toLowerCase().includes(q))
                  : String(p[pSearchCol] || '').toLowerCase().includes(q)
              );
            }

            // Column filters
            Object.entries(pFilters).forEach(([key, allowed]) => {
              processedList = processedList.filter(r => allowed.includes(String(r[key] || '').trim() || '__BLANK__'));
            });

            // Sort
            if (pSort.key) {
              processedList.sort((a, b) => {
                const av = String(a[pSort.key] || '');
                const bv = String(b[pSort.key] || '');
                return pSort.direction === 'ASC' ? av.localeCompare(bv) : bv.localeCompare(av);
              });
            }

            const totalProposalPages = Math.max(1, Math.ceil(processedList.length / PROPOSALS_PER_PAGE));
            const paginatedProposals = processedList.slice(
              (proposalPage - 1) * PROPOSALS_PER_PAGE,
              proposalPage * PROPOSALS_PER_PAGE
            );
            return (
              <div>
                {/* Search Toolbar */}
                {proposalList.length > 0 && (
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                      <div className="relative flex-grow group w-full lg:w-auto">
                        <div className="flex items-center rounded-lg border border-gray-300 bg-gray-50 focus-within:ring-2 focus-within:ring-yellow-500/20 focus-within:border-yellow-500 transition-all">
                          <select
                            className="h-10 pl-3 pr-1 text-[10px] font-bold uppercase bg-transparent border-r border-gray-300 rounded-l-lg outline-none appearance-none text-gray-400 focus:text-yellow-600"
                            value={pSearchCol}
                            onChange={e => { setPSearchCol(e.target.value); setProposalPage(1); }}
                          >
                            <option value="all">All Columns</option>
                            {PROPOSAL_COLS.map(c => (
                              <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                          </select>
                          <div className="relative flex-grow">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="text"
                              placeholder="Search proposals..."
                              value={pSearch}
                              onChange={e => { setPSearch(e.target.value); setProposalPage(1); }}
                              className="w-full h-10 pl-9 pr-8 bg-transparent rounded-r-lg text-sm focus:bg-white outline-none placeholder-gray-400 text-gray-700"
                            />
                            {pSearch && (
                              <button onClick={() => { setPSearch(''); setProposalPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                        {processedList.length} of {proposalList.length} proposals
                      </span>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  {loadingProposalList ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="w-6 h-6 animate-spin text-yellow-500 mb-2" />
                      <p className="text-xs font-medium text-gray-500">Loading proposals...</p>
                    </div>
                  ) : !filterCategory ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <FileText className="w-10 h-10 opacity-20 mb-3" />
                      <p className="text-sm font-medium text-gray-500">Please select a Category to begin</p>
                    </div>
                  ) : proposalList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <Search className="w-10 h-10 opacity-20 mb-3" />
                      <p className="text-sm font-medium text-gray-500">No proposals found</p>
                    </div>
                  ) : (
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr className="bg-yellow-500 text-white">
                          <th className="px-3 py-2.5 border-r border-yellow-400/50 w-20">
                            <span className="text-xs font-bold uppercase tracking-wider">Sr No.</span>
                          </th>
                          {PROPOSAL_COLS.map(col => (
                            <th key={col.key} className="px-3 py-2.5 border-r border-yellow-400/50 relative">
                              <div className="flex items-center justify-between cursor-pointer" onClick={() => setPSort(prev => ({ key: col.key, direction: prev.key === col.key && prev.direction === 'ASC' ? 'DESC' : 'ASC' }))}>
                                <span className="text-xs font-bold uppercase tracking-wider">{col.label}</span>
                                <div className="flex items-center">
                                  {pSort.key !== col.key ? <FaSort className="text-yellow-200 ml-1" size={10} /> : pSort.direction === 'ASC' ? <FaSortUp className="text-white ml-1" /> : <FaSortDown className="text-white ml-1" />}
                                  <div
                                    className={`ml-2 p-1 rounded cursor-pointer ${pFilters[col.key] ? 'text-white' : 'text-yellow-200 hover:text-white'}`}
                                    onClick={e => { e.stopPropagation(); setPActiveFilter(pActiveFilter === col.key ? null : col.key); }}
                                  >
                                    <FaFilter size={10} />
                                  </div>
                                </div>
                              </div>
                              {pActiveFilter === col.key && (
                                <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-2xl z-50 overflow-hidden">
                                  <div className="max-h-48 overflow-y-auto p-1">
                                    {getPUniqueValues(col.key).map(val => {
                                      const allowed = pFilters[col.key] || getPUniqueValues(col.key);
                                      return (
                                        <label key={val} className="flex items-center gap-2 px-2 py-1 hover:bg-yellow-50 cursor-pointer rounded text-xs text-gray-700">
                                          <input type="checkbox" checked={allowed.includes(val)}
                                            onChange={() => {
                                              setPFilters(prev => {
                                                const cur = prev[col.key] || getPUniqueValues(col.key);
                                                const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val];
                                                if (next.length === getPUniqueValues(col.key).length) { const c = { ...prev }; delete c[col.key]; return c; }
                                                return { ...prev, [col.key]: next };
                                              });
                                            }}
                                            className="w-3 h-3 text-yellow-500 rounded"
                                          />
                                          {val === '__BLANK__' ? '(Blank)' : val}
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div className="p-2 border-t border-gray-100 flex justify-end">
                                    <button onClick={() => { setPFilters(prev => { const c = { ...prev }; delete c[col.key]; return c; }); setPActiveFilter(null); }} className="text-xs text-yellow-600 font-semibold hover:underline">Clear</button>
                                  </div>
                                </div>
                              )}
                            </th>
                          ))}
                          <th className="px-3 py-2.5 text-center w-32">
                            <span className="text-xs font-bold uppercase tracking-wider">Details</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {paginatedProposals.map((p, idx) => (
                          <motion.tr
                            key={p.acquisition_proposal_id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: idx * 0.03 }}
                            className="group hover:bg-yellow-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-mono text-gray-500 group-hover:text-yellow-700 font-medium">
                                {(proposalPage - 1) * PROPOSALS_PER_PAGE + idx + 1}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-800">{p.acquisition_proposal_no}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-800">{p.section_11_bhusampadan_no}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-800">{p.dist_name}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-800">{p.taluka_name}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-gray-800">{p.village_name}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => {
                                  setSelectedProposal(String(p.acquisition_proposal_id));
                                  setCurrentPage(1);
                                  setViewMode('detail');
                                  fetchDashboardData(String(p.acquisition_proposal_id));
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-bold rounded-lg transition-colors shadow-sm"
                              >
                                <ExternalLink className="w-3 h-3" /> Details
                              </button>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Proposal Pagination */}
                {processedList.length > PROPOSALS_PER_PAGE && (
                  <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-medium">
                      Page <span className="text-gray-900">{proposalPage}</span> of {totalProposalPages} &nbsp;|&nbsp; {processedList.length} proposals         </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setProposalPage(1)}
                        disabled={proposalPage === 1}
                        className="px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold transition-all"
                      >First</button>
                      <button
                        onClick={() => setProposalPage(p => p - 1)}
                        disabled={proposalPage === 1}
                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      ><ChevronLeft size={15} /></button>
                      {Array.from({ length: Math.min(5, totalProposalPages) }, (_, i) => {
                        const start = Math.max(1, proposalPage - 2);
                        const page = start + i;
                        if (page > totalProposalPages) return null;
                        return (
                          <button
                            key={page}
                            onClick={() => setProposalPage(page)}
                            className={`w-7 h-7 text-xs font-bold rounded-lg border transition-all ${proposalPage === page ? 'bg-yellow-500 text-white border-yellow-500 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                          >{page}</button>
                        );
                      })}
                      <button
                        onClick={() => setProposalPage(p => p + 1)}
                        disabled={proposalPage === totalProposalPages}
                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      ><ChevronRight size={15} /></button>
                      <button
                        onClick={() => setProposalPage(totalProposalPages)}
                        disabled={proposalPage === totalProposalPages}
                        className="px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold transition-all"
                      >Last</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ─── Detail View ─── */}
          {viewMode === 'detail' && (<>

            {/* ─── Search Toolbar ─── */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                {/* Back Button */}
                <button
                  onClick={() => {
                    setViewMode('summary');
                    setSelectedProposal('');
                    setDashboardData([]);
                    setCurrentPage(1);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-yellow-50 hover:border-yellow-300 hover:text-yellow-700 transition-all text-xs font-semibold shadow-sm whitespace-nowrap"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Proposals
                </button>
                {/* Search Input */}
                <div className="relative flex-grow group w-full lg:w-auto">
                  <div className="flex items-center rounded-lg border border-gray-300 bg-gray-50 focus-within:ring-2 focus-within:ring-yellow-500/20 focus-within:border-yellow-500 transition-all">
                    <select
                      className="h-10 pl-3 pr-1 text-[10px] font-bold uppercase bg-transparent border-r border-gray-300 rounded-l-lg outline-none appearance-none transition-colors text-gray-400 focus:text-yellow-600"
                      value={searchColumn}
                      onChange={e => setSearchColumn(e.target.value)}
                    >
                      <option value="all">All Columns</option>
                      {columns.map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                    <div className="relative flex-grow">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-yellow-600 transition-colors" />
                      <input
                        type="text"
                        placeholder="Search records..."
                        value={searchText}
                        onChange={async (e) => {
                          const value = e.target.value;
                          setSearchText(value);
                          if (value.trim()) {
                            const marathiText = await convertToMarathi(value);
                            setConvertedSearchText(marathiText.toLowerCase());
                          } else {
                            setConvertedSearchText("");
                          }
                        }}
                        className="w-full h-10 pl-9 pr-8 bg-transparent rounded-r-lg text-sm focus:bg-white transition-all outline-none placeholder-gray-400 text-gray-700"
                      />
                      {searchText && (
                        <button onClick={() => setSearchText('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columns Dropdown */}
                <div className="relative" ref={colDropdownRef}>
                  <button
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all shadow-sm ${showColumnDropdown
                      ? 'bg-yellow-500 border-yellow-500 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                  >
                    <Columns3 className="w-4 h-4" />
                    <span className="text-xs font-bold">Columns</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showColumnDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showColumnDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: 5, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 ring-1 ring-black/5 z-50 overflow-hidden"
                      >
                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Toggle Columns</span>
                        </div>
                        <div className="p-1 max-h-72 overflow-y-auto">
                          {columns.map(c => (
                            <label
                              key={c.key}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-yellow-50 cursor-pointer rounded-md transition-colors text-sm font-medium text-gray-600"
                            >
                              <input
                                type="checkbox"
                                checked={c.visible}
                                onChange={() => setColumns(prev => prev.map(x => x.key === c.key ? { ...x, visible: !x.visible } : x))}
                                className="w-3.5 h-3.5 text-yellow-500 rounded border-gray-300 focus:ring-yellow-500"
                              />
                              {c.label}
                            </label>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* ─── Data Table ─── */}
            <div className="overflow-x-auto" ref={tableRef}>
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-yellow-500 text-white">
                    {columns.map((col, idx) => {
                      if (!col || !col.visible) return null;
                      return (
                        <th
                          key={col.key}
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, idx)}
                          onDragEnter={(e) => handleDragEnter(e, idx)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => e.preventDefault()}
                          className={`px-3 py-2.5 border-r border-yellow-400/50 relative group select-none transition-all ${draggingIdx === idx ? 'opacity-30 bg-yellow-600 border-dashed border-2 border-yellow-300' : ''}`}
                          style={{ width: col.width, cursor: 'grab' }}
                        >
                          <div className="flex items-center justify-between cursor-pointer" onClick={() => handleSort(col.key)}>
                            <span className="text-xs font-bold uppercase tracking-wider">{col.label}</span>
                            <div className="flex items-center">
                              {getSortIcon(col.key)}
                              {col.key !== 'sr_no' && (
                                <div
                                  className={`ml-2 p-1 rounded transition cursor-pointer ${excelFilters[col.key] ? 'text-white' : 'text-yellow-200 hover:text-white'}`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    toggleExcelFilter(col.key, e);
                                  }}                                >
                                  <FaFilter size={10} />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Column Resizer */}
                          <div
                            className="absolute right-0 top-0 w-1 h-full cursor-col-resize hover:bg-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={e => handleResizerMouseDown(e, idx)}
                          />

                          {/* Filter Dropdown */}
                          <AnimatePresence>
                            {activeFilterDropdown === col.key && (
                              <motion.div
                                ref={filterDropdownRef}
                                initial={{ opacity: 0, y: -5, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -5, scale: 0.98 }}
                                transition={{ duration: 0.15 }}
                                style={{
                                  position: "fixed",
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left,
                                  zIndex: 9999,
                                }}
                                className="w-64 bg-white border border-gray-200 shadow-2xl rounded-xl flex flex-col overflow-hidden text-[12px]"
                              >
                                <div className="py-2">
                                  <button onClick={() => { handleSort(col.key, 'ASC'); setActiveFilterDropdown(null); }} className="w-full px-4 py-2 flex items-center gap-3 hover:bg-yellow-50 text-gray-700 font-semibold text-[11px] transition-colors">
                                    <FaSortAlphaDown className="text-gray-400" /> Sort A to Z
                                  </button>
                                  <button onClick={() => { handleSort(col.key, 'DESC'); setActiveFilterDropdown(null); }} className="w-full px-4 py-2 flex items-center gap-3 hover:bg-yellow-50 text-gray-700 font-semibold text-[11px] transition-colors">
                                    <FaSortAlphaUp className="text-gray-400" /> Sort Z to A
                                  </button>
                                </div>
                                <div className="border-t border-gray-100 py-1.5">
                                  <button onClick={() => { setExcelFilters(p => { const x = { ...p }; delete x[col.key]; return x; }); setActiveFilterDropdown(null); }} className="w-full px-4 py-2 flex items-center gap-3 hover:bg-yellow-50 text-gray-700 font-semibold text-[11px] transition-colors">
                                    <span className="relative"><FaFilter className="text-gray-400" size={12} /><FaTimes size={6} className="absolute -bottom-0.5 -right-0.5 bg-gray-400 text-white rounded-full border border-white" /></span> Clear Filter
                                  </button>
                                </div>
                                <div className="border-t border-gray-100 p-3 pt-4">
                                  <input
                                    type="text"
                                    className="w-full h-8 px-3 border border-gray-200 rounded-lg text-[11px] outline-none mb-3 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-all"
                                    placeholder="Search..."
                                    value={checkListSearch}
                                    onChange={e => setCheckListSearch(e.target.value)}
                                  />
                                  <div className="border border-gray-100 rounded-lg p-1 max-h-48 overflow-y-auto">
                                    <label className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-yellow-50 cursor-pointer rounded transition-colors">
                                      <input type="checkbox" checked={!excelFilters[col.key]} onChange={() => setExcelFilters(p => { const x = { ...p }; delete x[col.key]; return x; })} className="w-3.5 h-3.5 text-yellow-500 rounded" />
                                      <span className="text-[11px] font-bold text-gray-600">(Select All)</span>
                                    </label>
                                    {getUniqueValues(col.key).filter(v => v.toLowerCase().includes(checkListSearch.toLowerCase())).map(v => (
                                      <label key={v} className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-yellow-50 cursor-pointer rounded transition-colors">
                                        <input type="checkbox" checked={!excelFilters[col.key] || excelFilters[col.key].includes(v)} onChange={() => handleExcelCheckChange(col.key, v)} className="w-3.5 h-3.5 text-yellow-500 rounded" />
                                        <span className="text-[11px] font-semibold text-gray-500 truncate">{v === '__BLANK__' ? '(Blanks)' : v}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="p-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/50">
                                  <button className="h-7 px-5 bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-[10px] rounded-md transition-colors shadow-sm" onClick={() => setActiveFilterDropdown(null)}>OK</button>
                                  <button className="h-7 px-4 bg-white border border-gray-200 text-gray-500 font-bold text-[10px] rounded-md hover:bg-gray-50 transition-colors" onClick={() => setActiveFilterDropdown(null)}>Cancel</button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  <AnimatePresence mode="wait">
                    {dashboardLoading ? (
                      <tr>
                        <td colSpan={columns.filter(c => c.visible).length} className="px-3 py-10 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-yellow-500 mb-2" />
                            <p className="text-xs font-medium text-gray-500">Loading publication records...</p>
                          </div>
                        </td>
                      </tr>
                    ) : dashboardError ? (
                      <tr>
                        <td colSpan={columns.filter(c => c.visible).length} className="px-3 py-10 text-center">
                          <div className="flex flex-col items-center justify-center text-red-500">
                            <X className="w-6 h-6 mb-2 opacity-40" />
                            <p className="text-xs font-medium">{dashboardError}</p>
                          </div>
                        </td>
                      </tr>
                    ) : dashboardData.length === 0 ? (
                      <tr>
                        <td colSpan={columns.filter(c => c.visible).length} className="px-3 py-10 text-center">
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <Search className="w-8 h-8 opacity-20 mb-2" />
                            <p className="text-sm font-medium text-gray-500">No records found</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">Select filters above to load data</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((row, idx) => (
                        <motion.tr
                          key={`${row.land_owner_id}-${idx}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: idx * 0.02 }}
                          className="group hover:bg-yellow-50 transition-colors"
                        >
                          {columns.map((col) => {
                            if (!col.visible) return null;
                            return (
                              <td key={col.key} className="px-3 py-2 border-r border-gray-100 last:border-r-0">
                                {col.key === "sr_no" ? (
                                  <span className="text-gray-500 text-sm font-mono group-hover:text-yellow-700 font-medium">
                                    {(currentPage - 1) * recordsPerPage + idx + 1}
                                  </span>
                                ) : col.key === "acquisition_proposal_date" ? (
                                  <span className="text-sm text-gray-600">{formatDate(row[col.key])}</span>
                                ) : col.key === "documents" ? (
                                  Array.isArray(row.documents) && row.documents.length > 0 ? (
                                    <button
                                      onClick={() => setDocModal(row.documents)}
                                      className="px-2.5 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-bold rounded transition-colors"
                                    >
                                      View
                                    </button>
                                  ) : (
                                    <span className="text-xs text-red-400 font-medium italic">No Document</span>
                                  )
                                ) : (
                                  <span className="text-sm text-gray-700">{row[col.key] ?? "-"}</span>
                                )}
                              </td>
                            )
                          })}
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* ─── Pagination ─── */}
            {/* <div className="px-3 py-2.5 border-t border-gray-200 bg-white flex items-center justify-between rounded-b-xl">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Page <span className="text-gray-900">{currentPage}</span> of {Math.max(1, totalPages)}
                </span>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">Show</span>
                  <select
                    value={recordsPerPage}
                    onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-xs font-medium text-gray-600 outline-none focus:border-yellow-500 transition-all"
                  >
                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm text-xs font-bold"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let end = Math.min(totalPages, start + maxVisible - 1);
                    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-7 h-7 text-xs font-bold rounded-lg transition-all border ${currentPage === i
                            ? 'bg-yellow-500 text-white border-yellow-500 shadow-md transform -translate-y-0.5'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                </div>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm text-xs font-bold"
                >
                  Last
                </button>
              </div>
            </div> */}
          </>)}
        </motion.div>

        {/* ─── Documents Modal ─── */}
        {docModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h2 className="text-base font-bold text-gray-800">Attached Documents</h2>
                <button onClick={() => setDocModal(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto divide-y divide-gray-100">
                {docModal.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No documents found.</p>
                ) : (
                  docModal.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-yellow-50">
                      <span className="text-sm text-gray-700 font-medium">{doc.section_type}</span>
                      <button
                        onClick={() => {
                          if (!doc?.file_path) return;
                          setDocPreviewUrl(doc.file_path);
                          setIsDocModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-bold rounded transition-colors"
                      >
                        <ExternalLink size={12} /> View
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        {isDocModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
              <div className="flex justify-between items-center px-5 py-3 bg-yellow-500 text-white">
                <h3 className="text-base font-bold">Document Preview</h3>
                <button
                  onClick={() => {
                    setIsDocModalOpen(false);
                    setDocPreviewUrl("");
                  }}
                  className="hover:bg-yellow-600 p-2 rounded transition"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 bg-gray-50 overflow-hidden">
                <iframe
                  src={docPreviewUrl}
                  title="Document Viewer"
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}